/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Button, ButtonGroup, Dialog, Typography2, breakpoints } from '@coursera/cds-core';

import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import CourseDialog from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/CourseDialog';
import {
  getUpdatedPlannedSegmentCoursesIds,
  useUpdateTermMutation,
} from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/lib/coursePlanningUtils';
import { useLearnerPlanStoreContext } from 'bundles/degree-home/components/v3-dpm/context/LearnerPlanStoreContext';
import type { RequirementTypeStore } from 'bundles/degree-home/components/v3-dpm/context/LearnerPlanStoreContext';
import type { UseCurriculum_PersonalizedSegmentCourseFragment as SegmentCourse } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import type { UseDegreePlanQuery as DegreePlanData } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useDegreePlan';
import useCreatePlanMutation from 'bundles/degree-home/components/v3-dpm/lib/useCreatePlanMutation';
import useCurriculum from 'bundles/degree-home/components/v3-dpm/lib/useCurriculum';
import useDegreePlan from 'bundles/degree-home/components/v3-dpm/lib/useDegreePlan';
import useMoveCourseMutation from 'bundles/degree-home/components/v3-dpm/lib/useMoveCourseMutation';

import _t from 'i18n!nls/degree-home';

const { Actions, Content, HeadingGroup } = Dialog;

export type CoursePlanDialogType = 'add' | 'move' | 'remove' | 'error' | null;

type CourseDialogType = {
  currentCourse: SegmentCourse | null;
  selectedTermId: string;
  sourceTermId: string;
};

type CoursePlanningDialogContextType = {
  openCourseDialog: (course: SegmentCourse | null, dialogType: CoursePlanDialogType, termId?: string) => void;
  closeDialog: () => void;
};

const CoursePlanningDialogContext = React.createContext<CoursePlanningDialogContextType | undefined>(undefined);

export const EMPTY_DEGREE_PLAN_DATA: DegreePlanData = {
  LearnerPlan: {
    queryPlan: {
      version: 1,
      learnerPlan: [],
    },
  },
};

const styles = {
  removeDialogAdditionalContent: css`
    > p {
      line-height: 36px;
    }

    margin-bottom: var(--cds-spacing-200);
  `,
  fullWidthMobileButtons: css`
    ${breakpoints.only('xs')} {
      width: 100%;
      max-width: none;
    }
  `,
};

export function useCoursePlanningDialog() {
  const context = React.useContext(CoursePlanningDialogContext);
  if (!context) {
    throw new Error('useCoursePlanningDialog must be used within a CoursePlanningDialogProvider');
  }
  return context;
}

const useTrackingData = (courseDialog: CourseDialogType, requirementTypeData: RequirementTypeStore | undefined) =>
  React.useMemo(() => {
    const currentCourseId = courseDialog.currentCourse?.segmentCourse?.id ?? '';
    const isElective = requirementTypeData?.[currentCourseId]?.isElective ?? undefined;

    let requirementType = '';

    if (isElective !== undefined) {
      requirementType = isElective ? 'Elective' : 'Required';
    }

    const courseName = courseDialog.currentCourse?.segmentCourse.course.courseName;
    const creditValue = courseDialog.currentCourse?.segmentCourse.creditValue;
    const termId = courseDialog.selectedTermId;
    const trackingData = {
      courseName,
      requirementType,
      courseCreditValue: creditValue,
      termId,
    };

    return trackingData;
  }, [courseDialog, requirementTypeData]);

export const CoursePlanningDialogProvider: React.FC = ({ children }) => {
  const [courseDialog, setCourseDialog] = React.useState<CourseDialogType>({
    currentCourse: null,
    selectedTermId: '',
    sourceTermId: '',
  });
  const [dialogType, setDialogType] = React.useState<CoursePlanDialogType>(null);
  // Initialize version to -1
  const [version, setVersion] = React.useState(-1);

  const { data: degreePlanData, refetch: refetchDegreePlan } = useDegreePlan();
  const { refetch: refetchCurriculum } = useCurriculum();
  const { requirementTypeData } = useLearnerPlanStoreContext();
  const { updateTerm, removeMutationLoading, updateMutationLoading } = useUpdateTermMutation();
  const { createPlan, createPlanLoading } = useCreatePlanMutation();
  const { moveCourse, moveCoursePlanLoading } = useMoveCourseMutation();
  const { degreeId } = useDegreeContext();
  const track = useRetracked();
  const trackingData = useTrackingData(courseDialog, requirementTypeData);

  React.useEffect(() => {
    if (degreePlanData?.LearnerPlan?.queryPlan) {
      const fetchedVersion = degreePlanData.LearnerPlan.queryPlan.version;
      if (fetchedVersion != null) {
        setVersion(fetchedVersion);
      }
    }
  }, [degreePlanData]);

  const openCourseDialog = React.useCallback(
    (course: SegmentCourse | null, dialogTypeToOpen: CoursePlanDialogType, termId?: string) => {
      setCourseDialog((prev) => ({
        ...prev,
        currentCourse: course,
        selectedTermId: termId ?? prev.selectedTermId,
        sourceTermId: termId || '',
      }));
      setDialogType(dialogTypeToOpen);
    },
    []
  );

  const closeDialog = React.useCallback(() => {
    if (dialogType === 'error') {
      refetchCurriculum();
      refetchDegreePlan();
    }
    setDialogType(null);
    setCourseDialog({ currentCourse: null, selectedTermId: '', sourceTermId: '' });
  }, [dialogType, refetchCurriculum, refetchDegreePlan]);

  const value = {
    openCourseDialog,
    closeDialog,
  };

  const handleTermSelected = (termId: string) => {
    setCourseDialog((prev) => ({ ...prev, selectedTermId: termId }));
  };

  const getDialogTitlesAndSupportText = (): {
    [type in Exclude<CoursePlanDialogType, null>]: { title: string; supportText: string; primaryBtnLabel: string };
  } => {
    return {
      add: {
        title: _t('Add to plan'),
        supportText: _t('Add course to graduation plan'),
        primaryBtnLabel: _t('Add course'),
      },
      move: {
        title: _t('Move course'),
        supportText: _t('Move course to another term'),
        primaryBtnLabel: _t('Move course'),
      },
      remove: {
        title: _t('Remove course'),
        supportText: _t('Remove a course from the graduation plan'),
        primaryBtnLabel: _t('Remove course'),
      },
      error: {
        title: _t('Oops! We ran into an issue completing your task.'),
        supportText: '',
        primaryBtnLabel: _t('Reload page'),
      },
    };
  };

  const getDialogAdditionalContent = (): {
    [type in Exclude<CoursePlanDialogType, null>]: React.ReactElement | null;
  } => {
    return {
      add: null,
      move: null,
      remove: (
        <div css={styles.removeDialogAdditionalContent}>
          <Typography2 component="h3" variant="subtitleMedium">
            {_t('Remove the following course from the graduation plan?')}
          </Typography2>
          <Typography2 component="p">{_t('You will be able to add this course later if you want.')}</Typography2>
        </div>
      ),
      error: (
        <Typography2 component="p">
          {_t(
            'Give the page a quick refresh and try again. If the problem persists, reach out to our support team for a helping hand.'
          )}
        </Typography2>
      ),
    };
  };

  const reloadPage = () => {
    window.location.reload();
  };

  const removeCourseFromTermHandler = async () => {
    if (!degreePlanData?.LearnerPlan?.queryPlan?.learnerPlan || !courseDialog.currentCourse) {
      setDialogType('error');
      closeDialog();
    }

    const currentCourseId = courseDialog.currentCourse?.segmentCourse?.id ?? '';
    const selectedTerm = degreePlanData?.LearnerPlan?.queryPlan?.learnerPlan?.find(
      (term) => term.estimatedTermId === courseDialog.selectedTermId
    );
    const updatedPlannedSegmentCoursesIds =
      selectedTerm?.plannedSegmentCourses
        ?.map((course) => course.personalizedSegmentCourse?.segmentCourse?.id)
        .filter((id): id is string => id !== undefined && id !== currentCourseId) ?? [];

    updateTerm({
      degreePlanData: degreePlanData as DegreePlanData,
      degreeId,
      selectedTermId: courseDialog.selectedTermId,
      updatedPlannedSegmentCoursesIds,
      version,
      currentCourse: courseDialog.currentCourse as SegmentCourse,
      refetchCurriculum,
      refetchDegreePlan,
      openCourseDialog,
      dialogType,
      track,
      trackingData,
    });

    closeDialog();
  };

  const addCourseToTermHandler = async () => {
    if (!courseDialog?.currentCourse) {
      setDialogType('error');
      closeDialog();
    }

    const currentCourseId = courseDialog.currentCourse?.segmentCourse?.id ?? '';

    let updatedDegreePlanData = degreePlanData;
    let updatedVersion = version;

    if (!updatedDegreePlanData) {
      const createPlanResponse = await createPlan({ openCourseDialog });

      if (!createPlanResponse) return;

      updatedDegreePlanData = EMPTY_DEGREE_PLAN_DATA;
      updatedVersion = createPlanResponse.version;
    }

    const existingTerm = updatedDegreePlanData?.LearnerPlan?.queryPlan?.learnerPlan?.find(
      (term) => term.estimatedTermId === courseDialog.selectedTermId
    );
    const updatedSegmentCourseIdsArray = getUpdatedPlannedSegmentCoursesIds(existingTerm, currentCourseId);

    updateTerm({
      degreePlanData: updatedDegreePlanData as DegreePlanData,
      degreeId,
      selectedTermId: courseDialog.selectedTermId,
      updatedPlannedSegmentCoursesIds: updatedSegmentCourseIdsArray,
      version: updatedVersion,
      currentCourse: courseDialog.currentCourse as SegmentCourse,
      refetchCurriculum,
      refetchDegreePlan,
      openCourseDialog,
      dialogType,
    });

    closeDialog();
  };

  const handleMoveCourse = async () => {
    if (!degreePlanData?.LearnerPlan?.queryPlan?.learnerPlan || !courseDialog.currentCourse) {
      setDialogType('error');
      closeDialog();
    }

    const moveCourseResponse = await moveCourse({
      openCourseDialog,
      destinationTermId: courseDialog.selectedTermId,
      sourceTermId: courseDialog.sourceTermId as string,
      selectedCourse: courseDialog.currentCourse as SegmentCourse,
      version,
      degreePlanData: degreePlanData as DegreePlanData,
    });

    if (!moveCourseResponse) return;

    track({ action: 'click', trackingName: 'move_course', trackingData });

    closeDialog();
  };

  const dialogOnClickHandlers: {
    [type in Exclude<CoursePlanDialogType, null>]: () => void;
  } = {
    add: addCourseToTermHandler,
    move: handleMoveCourse,
    remove: removeCourseFromTermHandler,
    error: reloadPage,
  };

  const dialogTitleSupportTextMap = getDialogTitlesAndSupportText();
  const dialogAdditionalContentMap = getDialogAdditionalContent();
  const dialogTitle = dialogType ? dialogTitleSupportTextMap[dialogType].title : '';
  const dialogHeaderSupportText = dialogType ? dialogTitleSupportTextMap[dialogType].supportText : '';
  const dialogPrimaryBtnLabel = dialogType ? dialogTitleSupportTextMap[dialogType].primaryBtnLabel : '';
  const dialogAdditionalContent = dialogType ? dialogAdditionalContentMap[dialogType] : null;
  const dialogColor = dialogType !== 'error' ? 'darkBlue' : undefined;
  const anyDialogOpen = dialogType !== null && (dialogType === 'error' || !!courseDialog.currentCourse);

  const disabledDueToSelectedId = dialogType !== 'remove' && !courseDialog.selectedTermId;
  const moveButtonDisabled = dialogType === 'move' && courseDialog.sourceTermId === courseDialog.selectedTermId;

  return (
    <CoursePlanningDialogContext.Provider value={value}>
      {children}
      {anyDialogOpen && (
        <Dialog open={anyDialogOpen} onClose={closeDialog} color={dialogColor} sticky="actions">
          <HeadingGroup supportText={dialogHeaderSupportText}>{dialogTitle}</HeadingGroup>
          <Content>
            {!!dialogAdditionalContent && dialogAdditionalContent}
            <CourseDialog
              course={courseDialog.currentCourse}
              dialogType={dialogType}
              selectedTerm={courseDialog.selectedTermId}
              onTermSelected={handleTermSelected}
              isError={dialogType === 'error'}
            />
          </Content>
          <Actions>
            {dialogType !== 'error' ? (
              <ButtonGroup
                primaryButtonProps={{
                  loading: createPlanLoading || removeMutationLoading || updateMutationLoading || moveCoursePlanLoading,
                  children: dialogPrimaryBtnLabel,
                  onClick: dialogType ? dialogOnClickHandlers[dialogType] : undefined, // TODO: implement handlers of on click for different dialog types
                  disabled: disabledDueToSelectedId || !!moveButtonDisabled,
                  css: styles.fullWidthMobileButtons,
                }}
                alternateButtonProps={{
                  children: _t('Cancel'),
                  onClick: closeDialog,
                  variant: 'secondary',
                  css: styles.fullWidthMobileButtons,
                }}
              />
            ) : (
              <Button css={styles.fullWidthMobileButtons} variant="primary" size="medium" onClick={reloadPage}>
                {dialogPrimaryBtnLabel}
              </Button>
            )}
          </Actions>
        </Dialog>
      )}
    </CoursePlanningDialogContext.Provider>
  );
};
